import { type Address } from 'viem';
import type { ConfidentialToken } from './useCofheTokenLists.js';
import { assertTokenOperationSupported, getConfidentialDecimals } from '@/types/token';
import { getTokenTypeContracts } from '../constants/tokenTypeConfig.js';
import { TransactionActionType, useTransactionStore } from '../stores/transactionStore.js';
import { type EncryptableItem } from '@cofhe/sdk';
import { useCofheEncryptAndWriteContract } from './useCofheEncryptAndWriteContract.js';
import type { useCofheWriteContractOptions } from './useCofheWriteContract.js';
import type { EncryptionOptions } from './useCofheEncrypt.js';
import { useTransactionGlobalLifecycle } from './useTransactionGlobalLifecycle.js';

type TokenTransferExtras = { token: ConfidentialToken; amount: bigint; userAddress: Address };
type UseCofheTokenTransferOptions = Pick<
  useCofheWriteContractOptions<TokenTransferExtras>,
  'onSuccess' | 'onError' | 'onMutate'
>;

type EncryptAndSendInput = {
  input: {
    token: ConfidentialToken;
    to: Address;
    amount: bigint;
    userAddress: Address;
  };
  encryptionOptions?: EncryptionOptions<EncryptableItem>;
};

export function useCofheTokenTransfer(writeMutationOptions?: UseCofheTokenTransferOptions) {
  const { onSuccess: passedOnSuccess, onError: passedOnError, ...restOfOptions } = writeMutationOptions || {};
  const { onTransactionSubmitError } = useTransactionGlobalLifecycle();
  const { encryption, write, encryptAndWrite } = useCofheEncryptAndWriteContract<TokenTransferExtras>({
    writingMutationOptions: {
      onError: (error, variables, onMutateResult, context) => {
        if (passedOnError) passedOnError(error, variables, onMutateResult, context);
        onTransactionSubmitError(error, TransactionActionType.ShieldSend);
      },
      onSuccess: (hash, variables, onMutateResult, context) => {
        if (passedOnSuccess) passedOnSuccess(hash, variables, onMutateResult, context);

        const extras =
          typeof variables === 'object' && variables !== null && 'extras' in variables ? variables.extras : null;

        if (!extras) return;

        // Record transaction and watch for confirmation
        useTransactionStore.getState().addTransaction({
          hash,
          token: extras.token,
          tokenAmount: extras.amount,
          tokenAmountDecimals: getConfidentialDecimals(extras.token),
          chainId: extras.token.chainId,
          actionType: TransactionActionType.ShieldSend,
          account: extras.userAddress,
        });
      },
      ...restOfOptions,
    },
  });

  return {
    encryption,
    write,
    isPending: encryption.isPending || write.isPending,
    data: write.data,
    encryptAmountAndSendToken: ({ input, encryptionOptions }: EncryptAndSendInput) => {
      const { token, to, amount, userAddress } = input;

      const { input: _ignoredInput, ...encryptionOpts } = encryptionOptions ?? {};
      assertTokenOperationSupported(token.extensions.fhenix.confidentialityType, 'transfer');
      const contractConfig = getTokenTypeContracts(token.extensions.fhenix.confidentialityType).confidentialTransfer;

      return encryptAndWrite({
        params: {
          address: token.address,
          abi: contractConfig.abi,
          functionName: contractConfig.functionName,
          account: userAddress,
          chain: undefined,
        },
        args: [to, amount] as const,
        extras: {
          token,
          amount,
          userAddress,
        },
        encryptionOptions: encryptionOpts,
      });
    },
  };
}
